import type { CalendarDate, IsoTimestamp } from '~/types/dates'
import type { MeasurementSessionId, PhaseId, WeeklyReviewId } from '~/types/ids'
import type { RecordEnvelope } from './common'
import type { MeasurementSiteKey } from './measurementSite'
import { ALGORITHM_IDS } from './schemaVersion'

/**
 * A weekly circumference session (D-046).
 *
 * Quality is stored PER SITE, never as one session-wide value. Only sites that
 * were actually recorded appear in `sites`; a skipped site is absent rather
 * than present-and-empty, so "not measured" and "measured badly" stay
 * distinguishable.
 */
export interface StoredSiteReading {
  /** All three readings, in entry order. Never discarded (D-016). */
  readonly readings: readonly number[]
  readonly medianCm: number
  readonly spreadCm: number
  readonly exceedsThreshold: boolean
  readonly thresholdUsedCm: number
}

export type BodyFatStatus = 'ok' | 'implausible' | 'invalid'

/**
 * Values derived at save time.
 *
 * Stored alongside their algorithm versions so the numbers stay interpretable
 * after a formula changes. There is deliberately NO practical-range field:
 * Version 1 neither calculates nor displays a numerical range (D-015).
 */
export interface DerivedMeasurementSnapshot {
  readonly bodyFatStatus: BodyFatStatus
  readonly bodyFatPercent?: number
  readonly bodyFatInvalidReason?: string
  readonly waistToHeightRatio?: number
  readonly fatMassKg?: number
  readonly leanMassKg?: number
  /** The inputs the derived values came from, so they can be audited later. */
  readonly sourceHeightCm: number
  readonly sourceWeightKg?: number
  readonly algorithmVersions: Readonly<Record<string, string>>
}

export interface MeasurementSessionRecord extends RecordEnvelope {
  readonly id: MeasurementSessionId
  readonly date: CalendarDate
  readonly sites: Readonly<Partial<Record<MeasurementSiteKey, StoredSiteReading>>>
  readonly derived: DerivedMeasurementSnapshot
  readonly reviewId?: WeeklyReviewId
  readonly phaseId?: PhaseId
  readonly conditionNote?: string
  readonly recordedAt: IsoTimestamp
}

export const MEASUREMENT_ALGORITHM_VERSIONS: Readonly<Record<string, string>> = {
  bodyFat: ALGORITHM_IDS.bodyFat,
}
